import {
  parseMemberMigrationState,
  type MemberMigrationState,
} from "./member-bootstrap";

const WATCHER_HQ_ORIGIN = "https://watchersociety.com";
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const HASH = /^0x[0-9a-fA-F]{64}$/;
const INTENT_BINDING_TOKEN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const ENTRY_POINT = "0x0000000071727De22E5E9d8BAf0edAc6f37da032";

function watcherHqUrl(path: string): string {
  if (
    typeof window !== "undefined" &&
    window.location.origin === WATCHER_HQ_ORIGIN
  ) {
    return path;
  }
  return `${WATCHER_HQ_ORIGIN}${path}`;
}

export async function memberBundlerRequest(
  chainId: number,
  method: string,
  params: unknown[],
  memberAuthToken: string,
  signal?: AbortSignal,
  intentBindingToken?: string,
) {
  if (memberAuthToken.length > 8192 || !JWT_SHAPE.test(memberAuthToken)) {
    throw new Error("MEMBER_SESSION_UNAVAILABLE");
  }
  if (
    intentBindingToken !== undefined &&
    !INTENT_BINDING_TOKEN.test(intentBindingToken)
  ) {
    throw new Error("MEMBER_INTENT_BINDING_INVALID");
  }
  const response = await fetch(
    watcherHqUrl(`/api/wallet/bundler?chainId=${chainId}`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${memberAuthToken}`,
        "Content-Type": "application/json",
      },
      credentials: "omit",
      signal,
      body: JSON.stringify(
        {
          jsonrpc: "2.0",
          id: 1,
          method,
          params,
          ...(intentBindingToken ? { intentBindingToken } : {}),
        },
        (_key, value) =>
          typeof value === "bigint" ? `0x${value.toString(16)}` : value,
      ),
    },
  );
  const body = await response.json();
  if (
    !response.ok ||
    body?.jsonrpc !== "2.0" ||
    body.id !== 1 ||
    body.error ||
    !("result" in body)
  ) {
    throw new Error(body.error?.message || "MEMBER_BUNDLER_UNAVAILABLE");
  }
  return body.result;
}

export async function readMemberMigrationState(
  memberAuthToken: string,
  signal?: AbortSignal,
): Promise<MemberMigrationState> {
  return parseMemberMigrationState(
    await memberBundlerRequest(
      8453,
      "watcher_getMemberMigrationState",
      [],
      memberAuthToken,
      signal,
    ),
  );
}

function gasTownApiUrl(path: string): string {
  const origin = (
    import.meta.env.VITE_GASTOWN_API_URL ||
    "https://api.gastown.watchersociety.com"
  ).replace(/\/$/, "");
  return `${origin}${path}`;
}

/** Read-only chain access through the existing authenticated Gas Town RPC boundary. */
export async function memberChainRequest(
  chainId: number,
  method: string,
  params: unknown[],
  memberAuthToken: string,
) {
  if (memberAuthToken.length > 8192 || !JWT_SHAPE.test(memberAuthToken)) {
    throw new Error("MEMBER_SESSION_UNAVAILABLE");
  }
  const response = await fetch(gasTownApiUrl("/wallet/rpc"), {
    method: "POST",
    credentials: "omit",
    headers: {
      Authorization: `Bearer ${memberAuthToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      chainId,
      request: { jsonrpc: "2.0", id: 1, method, params },
    }),
  });
  const body = await response.json();
  if (
    !response.ok ||
    body?.jsonrpc !== "2.0" ||
    body.id !== 1 ||
    body.error ||
    !("result" in body)
  ) {
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
  return body.result;
}

export async function estimateMemberOperation(
  chainId: number,
  userOperation: object,
  memberAuthToken: string,
) {
  return memberBundlerRequest(
    chainId,
    "eth_estimateUserOperationGas",
    [userOperation, ENTRY_POINT],
    memberAuthToken,
  );
}

export async function getMemberOperationGasPrice(
  chainId: number,
  memberAuthToken: string,
) {
  return memberBundlerRequest(
    chainId,
    "pimlico_getUserOperationGasPrice",
    [],
    memberAuthToken,
  );
}

export async function sendMemberOperation(
  chainId: number,
  userOperation: object,
  memberAuthToken: string,
  intentBindingToken: string,
): Promise<`0x${string}`> {
  const result = await memberBundlerRequest(
    chainId,
    "eth_sendUserOperation",
    [userOperation, ENTRY_POINT],
    memberAuthToken,
    undefined,
    intentBindingToken,
  );
  if (typeof result !== "string" || !HASH.test(result)) {
    throw new Error("MEMBER_BUNDLER_RESPONSE_INVALID");
  }
  return result as `0x${string}`;
}

export async function getPendingMemberOperation(
  chainId: number,
  memberAuthToken: string,
) {
  return memberBundlerRequest(
    chainId,
    "watcher_getPendingUserOperation",
    [],
    memberAuthToken,
  ) as Promise<{ state: string; userOpHash: string; nonce: string } | null>;
}

export async function retryPendingMemberOperation(
  chainId: number,
  memberAuthToken: string,
) {
  return memberBundlerRequest(
    chainId,
    "watcher_retryPendingUserOperation",
    [],
    memberAuthToken,
  ) as Promise<{
    userOpHash: string;
    status: "submitted" | "terminal" | "aborted" | "pending_reconciliation";
    receipt?: unknown;
  } | null>;
}

export async function getMemberOperationReceipt(
  chainId: number,
  userOperationHash: string,
  memberAuthToken: string,
) {
  if (!HASH.test(userOperationHash)) {
    throw new Error("MEMBER_BUNDLER_RESPONSE_INVALID");
  }
  return memberBundlerRequest(
    chainId,
    "eth_getUserOperationReceipt",
    [userOperationHash],
    memberAuthToken,
  );
}

export async function sponsorMemberOperation(
  chainId: number,
  userOperation: object,
  memberAuthToken: string,
) {
  // The existing Watcher account factory is EntryPoint v0.7.
  const result = await memberBundlerRequest(
    chainId,
    "pm_sponsorUserOperation",
    [userOperation, ENTRY_POINT],
    memberAuthToken,
  );
  const gas = (key: string) =>
    result[key] !== undefined ? BigInt(result[key]) : undefined;
  if (
    typeof result.paymaster !== "string" ||
    typeof result.paymasterData !== "string"
  ) {
    throw new Error("MEMBER_PAYMASTER_RESPONSE_INVALID");
  }
  return {
    paymaster: result.paymaster as string,
    paymasterData: result.paymasterData as string,
    callGasLimit: gas("callGasLimit"),
    verificationGasLimit: gas("verificationGasLimit"),
    preVerificationGas: gas("preVerificationGas"),
    paymasterVerificationGasLimit: gas("paymasterVerificationGasLimit"),
    paymasterPostOpGasLimit: gas("paymasterPostOpGasLimit"),
  };
}
